<template>
    <div>
        <slot />

        <panel-stack
            :panels="panels"
            :react-to-events="shouldPanelsReactToEvents()"
        />

        <modal-layer
            v-for="(modal, idx) in modalStack"
            :key="modal.key || `modal-${idx}`"
            style="z-index: 300"
            :class="modal.layerClass"
            :handle-focus="modal.focus"
            :react-to-events="shouldModalReactToEvents(idx)"
            :skip-trapping-focus-when-this-element-gets-focus="
                modal.skipTrappingFocusWhenThisElementGetsFocus
            "
            @escape="modal.onEscape"
            @click-outside="modal.onClickOutside"
        >
            <component
                :is="modal.component"
                :class="modal.klass"
                v-bind="modal.props"
                v-on="modal.on"
            />
        </modal-layer>

        <modal-layer
            v-if="dialog"
            style="z-index: 400"
            class="bg--black--50"
            :react-to-events="shouldDialogReactToEvents()"
            :handle-focus="dialog.focus"
            @escape="dialog.onEscape"
            @click-outside="dialog.onClickOutside"
        >
            <dialog-box
                :icon="dialog.icon"
                :title="dialog.title"
                :description="dialog.description"
                :mode="dialog.mode"
                :options="dialog.options"
                @done="dialog.onDone"
            />
        </modal-layer>

        <transition-group
            name="fade"
            tag="div"
            style="z-index: 500"
            class="w-100 position-fixed notification--container"
            appear
        >
            <notification-item
                v-for="notification in notifications"
                :key="notification.id"
                :notification="notification"
                @click.left="dismissNotification(notification)"
            />
        </transition-group>

        <light-pop-up
            v-for="(popup, index) in popups"
            :key="popup.id"
            style="z-index: 600"
            :popup="popup"
            class="position-fixed top left"
            :react-to-events="index === popups.length - 1"
            @ref-disappeared="() => removePopup(popup.id)"
        />
    </div>
</template>

<script>
import { mapState } from "vuex"

import panelsMixin from "~/mixins/panelsMixin"
import modalsMixin from "~/mixins/modalsMixin"

import { usePopups, usePopupActions } from "front-toolkit/plugins/PopupPlugin"
import { useFtkDataProvider } from "~/composables/useFtkDataProvider"

import AlertModal from "~/components/DialogBox/AlertModal"
import DialogBox from "~/components/DialogBox/DialogBox"
import LightPopUp from "front-toolkit/components/LightPopUp"
import ModalLayer from "~/components/ModalLayer"
import NotificationItem from "~/components/elements/NotificationItem"
import PanelStack from "~/components/PanelStack"

export default {
    mixins: [panelsMixin, modalsMixin],

    provide() {
        return {
            alert: this.alert,
            confirm: this.confirm,
            error: this.error,
            notify: this.notify,
        }
    },

    props: {
        hasRequiredAlertFields: Boolean,
        timeZone: String,
        alertFields: {
            type: Array,
            default: () => [],
        },
    },

    emits: ["update:alert-fields"],

    setup() {
        const { popups } = usePopups()
        const { removePopup } = usePopupActions()
        const { provideFtkFields } = useFtkDataProvider()

        provideFtkFields()

        return {
            popups,
            removePopup,
        }
    },

    data() {
        return {
            dialog: null,
        }
    },

    computed: {
        ...mapState(["notifications"]),
    },

    watch: {
        $route(to, from) {
            if (to.path != from.path) {
                this.modalStack = this.modalStack.filter(
                    modal => modal.isPersistent
                )

                Object.keys(this.panels).forEach(panelName => {
                    this.popSidePanel(panelName)
                })
            }
        },

        hasRequiredAlertFields: {
            immediate: true,
            handler(value) {
                if (!value) return

                this.showAlertModal()
            },
        },

        // from panelsMixin
        panels: {
            handler() {
                this.updateScrollbars()
            },
            deep: true,
        },

        // from modalsMixin
        modalStack: {
            handler() {
                this.updateScrollbars()
            },
            deep: true,
        },

        dialog: {
            handler() {
                this.updateScrollbars()
            },
            deep: true,
        },
    },

    methods: {
        shouldPanelsReactToEvents() {
            if (this.popups.length > 0) return false
            if (this.dialog) return false
            if (this.modalStack.length > 0) return false

            return true
        },

        shouldModalReactToEvents(index) {
            if (this.popups.length > 0) return false
            if (this.dialog) return false

            return index === this.modalStack.length - 1
        },

        shouldDialogReactToEvents() {
            if (this.popups.length > 0) return false

            return true
        },

        async showAlertModal() {
            this.pushModal(AlertModal, {
                isPersistent: true,
                props: {
                    alertFields: this.alertFields,
                    timeZone: this.timeZone,
                },
                onClickOutside: () => {}, // Do nothing
                onEscape: () => {}, // Do nothing
                on: {
                    submit: formData => {
                        this.$emit(
                            "update:alert-fields",
                            formData,
                            this.popModal
                        )
                    },
                },
            })
        },

        updateScrollbars() {
            const html = document.documentElement

            const hasDialog = !!this.dialog
            const hasModals = this.modalStack.length > 0
            const hasPanels = Object.values(this.panels).some(
                p => p.blockScroll
            )

            if (hasDialog || hasModals || hasPanels) {
                html.style.overflow = "hidden"
            } else {
                html.style.overflow = ""
            }
        },

        dismissNotification(notification) {
            this.$store.commit("removeNotification", notification.id)
        },

        /**
         * See createDialog.
         */
        alert(params) {
            return this.createDialog({ ...params, mode: "alert" })
        },

        /**
         * See createDialog.
         */
        confirm(params) {
            return this.createDialog({ ...params, mode: "confirmation" })
        },

        /**
         * See createDialog.
         */
        notify(params) {
            return this.createDialog({ ...params, mode: "notification" })
        },

        error(description) {
            return this.createDialog({
                title: this.$t("common.error"),
                description,
                icon: "close",
                mode: "alert",
            })
        },

        /**
         * * title:          DialogBox title.
         * * description:    DialogBox description.
         * * icon:           Optional. DialogBox icon.
         * * options: {
         *     confirmText?: String
         *     cancelText?: String
         *   }
         * * mode:           "alert" | "confirmation" | "notification"
         * * onClickOutside: A function to be called when clicking outside the dialog.
         *                   Defaults to removing the dialog.
         *                   You probably want to use `onClose`.
         * * onEscape:       A function to be called when pressing Escape.
         *                   Defaults to removing the dialog.
         *                   You probably want to use `onClose`.
         * * onClose         A function to be called when the dialog is closed.
         * * focus:          Give focus to the dialog, keep it inside and restore it when closed.
         *
         * returns:          A Promise resolving to ...
         */
        createDialog({
            title,
            description,
            icon,
            options,
            mode,
            onClickOutside,
            onEscape,
            onClose,
            focus = true,
        }) {
            const deleteDialog = () => {
                const { dialog } = this
                if (!dialog) return
                this.dialog = null
                dialog.onClose?.()
                if (dialog.focus) {
                    this.$nextTick(() => {
                        // Do this after the dialog has been destroyed,
                        // so its focus handler doesn't kick in.
                        // document.activeElement can be null if the document doesn't have the focus
                        dialog.restoreFocusEl?.focus()
                    })
                }
            }

            return new Promise(resolve => {
                this.dialog = {
                    restoreFocusEl: document.activeElement,
                    title,
                    description,
                    icon,
                    mode,
                    options,
                    focus,
                    onClickOutside:
                        onClickOutside ??
                        (event => {
                            event.stopPropagation()
                            deleteDialog()
                        }),
                    onEscape:
                        onEscape ??
                        (event => {
                            event.stopPropagation()
                            deleteDialog()
                        }),
                    onClose,
                    onDone: res => {
                        deleteDialog()
                        resolve(res)
                    },
                }
            })
        },
    },

    components: {
        DialogBox,
        ModalLayer,
        PanelStack,
        NotificationItem,
        LightPopUp,
    },
}
</script>

<style lang="scss" scoped>
@import "~/scss/bootstrap-importable";

.notification--container {
    @include media-breakpoint-down(xs) {
        max-width: 100%;
        padding-left: 10px;
        padding-right: 10px;
        top: 10px;
    }

    @include media-breakpoint-only(s) {
        max-width: 50%;
    }

    @include media-breakpoint-up(s) {
        bottom: 20px;
        padding-left: 20px;
        padding-right: 20px;
    }

    @include media-breakpoint-up(sm) {
        max-width: 35%;
    }
}
</style>

<style>
.vc-date-picker-content {
    background-color: transparent;
}
/* v-calendar custom colors */
.vc-primary {
    --vc-accent-50: v-bind($store.getters[ "theme/primaryColorLight90"]);
    --vc-accent-100: v-bind($store.getters[ "theme/primaryColorLight90"]);
    --vc-accent-200: v-bind($store.getters[ "theme/primaryColorLight50"]);
    --vc-accent-300: v-bind($store.getters[ "theme/primaryColorLight30"]);
    --vc-accent-400: v-bind($store.getters[ "theme/primaryColorLight25"]);
    --vc-accent-500: v-bind($store.getters[ "theme/primaryColorLight15"]);
    --vc-accent-600: v-bind(
        $store.getters[ "theme/primaryColor"]
    ); /* Main color used to highlight days */
    --vc-accent-700: v-bind($store.getters[ "theme/primaryColorDark10"]);
    --vc-accent-800: v-bind($store.getters[ "theme/primaryColorDark15"]);
    --vc-accent-900: v-bind($store.getters[ "theme/primaryColorDark30"]);
}
</style>
